'use client';

import { useEffect, useRef, useState, type ComponentProps } from 'react';

import { XIcon } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn, FOCUS_RING_TEXT } from '@/lib/utils';

/** Matches the platform's search input, so clearing feels the same in both apps. */
const WORD_MS = 420;
const WORD_STAGGER_MS = 55;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';

type Props = Omit<ComponentProps<typeof Input>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (next: string) => void;
  clearLabel?: string;
};

/**
 * A text field whose clear button dissolves the text rather than blanking it: the words lift, fade
 * and blur away a beat apart. The field itself is emptied immediately — the animation plays on a
 * mirror layered over it, so nothing about the real input's state waits on it.
 */
export function ClearableInput({
  value,
  onValueChange,
  clearLabel = 'Clear',
  className,
  ...props
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const [clearing, setClearing] = useState<string | null>(null);

  useEffect(
    function dissolve() {
      if (clearing == null) return;
      const mirror = mirrorRef.current;
      const input = inputRef.current;
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (!mirror || !input || typeof mirror.animate !== 'function' || reduce) {
        setClearing(null);
        return;
      }

      // Copy the input's own text rendering, so the dissolving glyphs sit exactly where they were
      // typed rather than in a second font a few pixels off.
      const styles = getComputedStyle(input);
      for (const prop of [
        'fontFamily',
        'fontSize',
        'fontWeight',
        'letterSpacing',
        'paddingLeft',
        'paddingRight',
        'color',
      ] as const) {
        mirror.style[prop] = styles[prop];
      }

      const words = Array.from(mirror.querySelectorAll<HTMLElement>('[data-word]'));
      let cancelled = false;
      const running = (words.length ? words : [mirror]).map((el, index) =>
        el.animate(
          [
            { transform: 'translateY(0)', opacity: 1, filter: 'blur(0px)' },
            { transform: 'translateY(-9px)', opacity: 0, filter: 'blur(3px)' },
          ],
          { duration: WORD_MS, delay: index * WORD_STAGGER_MS, easing: EASING, fill: 'forwards' },
        ),
      );
      Promise.all(running.map(animation => animation.finished))
        .then(() => !cancelled && setClearing(null))
        .catch(() => {});
      return function stop() {
        cancelled = true;
        running.forEach(animation => animation.cancel());
      };
    },
    [clearing],
  );

  function clear() {
    setClearing(value);
    onValueChange('');
    inputRef.current?.focus();
  }

  return (
    <div className="relative flex-1">
      <Input
        ref={inputRef}
        value={value}
        onChange={event => onValueChange(event.target.value)}
        className={cn(value && 'pe-10', className)}
        {...props}
      />
      {clearing != null && (
        <div
          ref={mirrorRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-10 flex items-center overflow-hidden whitespace-nowrap"
        >
          {clearing.split(/(\s+)/).map((part, index) => (
            <span
              key={index}
              className="inline-block whitespace-pre"
              {...(part.trim() ? { 'data-word': '' } : {})}
            >
              {part}
            </span>
          ))}
        </div>
      )}
      {value && (
        <button
          type="button"
          onClick={clear}
          aria-label={clearLabel}
          className={cn(
            'absolute end-1 top-1/2 z-20 flex size-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full',
            'text-muted-foreground transition-colors duration-200 hover:text-foreground',
            FOCUS_RING_TEXT,
          )}
        >
          <XIcon className="size-4" />
        </button>
      )}
    </div>
  );
}
